import { getPopularProducts } from '@koala/sdk/v4';
import { QueryClient, Hydrate } from '@tanstack/react-query';
import { type InferGetServerSidePropsType, type GetServerSideProps } from 'next';
import { type SSRData } from 'next-urql';
import { getConfigOverride } from '@/features/configs/configs';
import { getOrganization } from '@/features/configs/organization';
import LocationsFinder from '@/features/locations/finder';
import { refuseUnknownStorefrontPage } from '@/server/storefrontHostnames';
import { createHttpClient } from '@/services/client';
import { getFooterTemplate } from '@/services/footer.service';
import { type ServerQuery, queryOnServer } from '@/services/ssr.service';
import { getOrigin } from '@/utils';
import { SSR_RETRY_OPTIONS } from '@/utils/requests';
import { isUrlServiceTypeDelivery } from '@/utils/urls';
import { retryGuardedRead } from '@/utils/withJsonGuard';

function Index({ serverState }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <Hydrate state={serverState}>
      {/* @ts-expect-error */}
      <LocationsFinder />
    </Hydrate>
  );
}

interface PageProps {
  urqlState?: SSRData;
  serverState?: unknown;
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({ req, res, query }) => {
  if (!req.headers.host) {
    throw new Error('Missing request origin');
  }

  const refused = await refuseUnknownStorefrontPage(req, res);
  if (refused) {
    return refused;
  }

  const queryClient = new QueryClient();
  const origin = getOrigin(req.headers.host);
  const organizationConfig = await getOrganization(queryClient, origin);
  const isSingleLocation = organizationConfig?.locations.features.isSingleLocation;

  const isSingleLocationEnabled = Boolean(
    isSingleLocation.enabled && isSingleLocation.storeLocationId,
  );

  /**
   * Redirect to store page if it is single store location organization/brand
   */
  if (isSingleLocationEnabled) {
    const urlParams = isUrlServiceTypeDelivery(query) ? '?service_type=delivery' : '';
    return {
      redirect: {
        destination: `/store/${isSingleLocation.storeLocationId}${urlParams}`,
        permanent: false,
      },
    };
  }

  const { flagship_store_location } = await getConfigOverride('menu', queryClient, origin);

  let serverQueries: ServerQuery[] = [
    {
      queryKey: ['site-footer'],
      queryFn: () => getFooterTemplate({ origin, orgId: organizationConfig?.organization.id }),
    },
  ];

  if (flagship_store_location) {
    serverQueries = serverQueries.concat([
      {
        queryKey: ['popular-items', { id: flagship_store_location }],
        queryFn: () =>
          retryGuardedRead(
            () =>
              getPopularProducts(
                { locationId: flagship_store_location, maxItems: 15 },
                { client: createHttpClient({ origin }) },
              ),
            {
              route: '/',
              urlHint: '/v1/ordering/recommendations/products/popular',
              locationId: flagship_store_location.toString(),
            },
            SSR_RETRY_OPTIONS,
          ),
      },
    ]);
  }

  const serverState = await queryOnServer(serverQueries, queryClient);

  return {
    props: {
      serverState,
    },
  };
};

export default Index;
